import { describePolyMarketCached } from "@/lib/polymarket/describeOrder";
import {
  formatMergeAtomic,
  type PolyMergeAccounting,
} from "@/lib/polymarket/mergeAccounting";

export function PolymarketMergeHistory({
  rows,
  unavailable = false,
  retained = false,
  hidePnl = false,
  loading = false,
}: {
  rows: PolyMergeAccounting[];
  unavailable?: boolean;
  retained?: boolean;
  hidePnl?: boolean;
  loading?: boolean;
}) {
  const money = (cents: number | null) =>
    hidePnl ? "•••" : cents == null ? "—" : `$${(cents / 100).toFixed(2)}`;
  return (
    <section
      aria-label="Polymarket merges"
      className="bg-card border-border min-w-0 rounded-lg border"
    >
      <div className="border-border border-b px-4 py-2.5">
        <h2 className="text-foreground text-sm font-medium">
          Polymarket Merges
        </h2>
        <p className="text-muted-foreground mt-1 text-xs">
          Both outcomes close together. Gross P&amp;L is reported once per
          condition; net P&amp;L remains unavailable while execution fees are
          unknown. Merges are excluded from trading volume.
        </p>
        {unavailable && (
          <p
            role="status"
            className="mt-1 text-xs text-amber-600 dark:text-amber-400"
          >
            {retained
              ? "Showing the last confirmed merge history; refresh unavailable."
              : "Merge history unavailable."}
          </p>
        )}
      </div>
      {rows.length === 0 ? (
        <p className="text-muted-foreground px-4 py-4 text-sm">
          {loading
            ? "Loading merge history…"
            : unavailable
            ? "No confirmed merge history to show."
            : "No merges in this view."}
        </p>
      ) : (
        <div
          className="overflow-x-auto"
          tabIndex={0}
          aria-label="Merge accounting table"
        >
          <table className="w-full text-xs">
            <thead className="text-muted-foreground border-border border-b text-left">
              <tr>
                {[
                  "Condition",
                  "YES consumed / cost",
                  "NO consumed / cost",
                  "Collateral received",
                  "Gross P&L",
                  "Net P&L",
                  "Executed",
                ].map((label) => (
                  <th
                    key={label}
                    className="whitespace-nowrap px-4 py-2 font-medium"
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.intent_id}
                  className="border-border/40 border-b last:border-0"
                >
                  <td className="max-w-72 break-words px-4 py-3">
                    <span>
                      {describePolyMarketCached(row.ticker)?.matchup ??
                        row.ticker}
                    </span>
                    <span className="text-muted-foreground mt-1 block break-all text-[10px]">
                      {row.condition_id}
                    </span>
                    {row.accounting_status === "conflict" && (
                      <span className="mt-1 block text-amber-600 dark:text-amber-400">
                        Accounting conflict — costs and P&amp;L withheld
                      </span>
                    )}
                  </td>
                  {[row.yes_cost_cents, row.no_cost_cents].map((cost, i) => (
                    <td
                      key={i}
                      className="whitespace-nowrap px-4 py-3 font-mono"
                    >
                      {formatMergeAtomic(row.quantity_atomic)}
                      <span className="text-muted-foreground mt-1 block">
                        {money(
                          row.accounting_status === "conflict" ? null : cost,
                        )}
                      </span>
                    </td>
                  ))}
                  <td className="whitespace-nowrap px-4 py-3 font-mono">
                    {hidePnl
                      ? "•••"
                      : `$${formatMergeAtomic(row.collateral_atomic)}`}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 font-mono">
                    {money(
                      row.accounting_status === "conflict"
                        ? null
                        : row.gross_pnl_cents,
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                    {hidePnl ? "•••" : row.accounting_status === "conflict" ? "Unavailable" : "Unknown fees"}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                    {new Date(row.executed_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
